import { extractApiErrorMessage } from './apiErrorUtils';

const CASH_DOCUMENTS_ERROR_MESSAGES: Record<string, string> = {
  CASH_DOCUMENT_DUPLICATE_DOCUMENT_NUMBER:
    'Numer dokumentu kasowego został już zajęty. Odśwież dane i spróbuj ponownie.',
  CASH_DOCUMENT_MISSING_OPEN_REPORT_CYCLE:
    'Brak otwartego raportu kasowego dla wybranego okresu. Otwórz raport kasowy i spróbuj ponownie.',
};

interface ProblemPayloadShape {
  errorCode?: unknown;
  detail?: unknown;
  message?: unknown;
  errors?: unknown;
}

const getErrorStatus = (error: unknown): number | string | undefined => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return undefined;
  }

  const status = (error as { status?: unknown }).status;
  if (typeof status === 'number' || typeof status === 'string') {
    return status;
  }

  return undefined;
};

const getProblemPayload = (error: unknown): ProblemPayloadShape | null => {
  if (typeof error !== 'object' || error === null || !('data' in error)) {
    return null;
  }

  const data = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) {
    return null;
  }

  return data as ProblemPayloadShape;
};

const getProblemErrorCode = (error: unknown): string | undefined => {
  const payload = getProblemPayload(error);
  if (!payload || typeof payload.errorCode !== 'string' || !payload.errorCode.trim()) {
    return undefined;
  }

  return payload.errorCode.trim();
};

const getProblemMessages = (error: unknown): string[] => {
  const payload = getProblemPayload(error);
  if (!payload) {
    return [];
  }

  const messages: string[] = [];

  if (typeof payload.detail === 'string' && payload.detail.trim()) {
    messages.push(payload.detail.trim());
  }

  if (typeof payload.message === 'string' && payload.message.trim()) {
    messages.push(payload.message.trim());
  }

  if (typeof payload.errors === 'object' && payload.errors !== null) {
    Object.values(payload.errors as Record<string, unknown>).forEach((value) => {
      if (!Array.isArray(value)) {
        return;
      }

      value.forEach((item) => {
        if (typeof item === 'string' && item.trim()) {
          messages.push(item.trim());
        }
      });
    });
  }

  return messages;
};

const mapBackendMessageToOperatorMessage = (message: string): string | null => {
  if (message.includes('does not belong to Reservation')) {
    return 'Wybrana płatność nie należy do tej rezerwacji. Odśwież dane rezerwacji i spróbuj ponownie.';
  }

  if (message.includes('is already linked to CashDocument')) {
    return 'Ta płatność jest już powiązana z innym dokumentem kasowym.';
  }

  if (message.includes('must use cash payment method')) {
    return 'Z dokumentem kasowym można powiązać tylko płatność oznaczoną jako gotówkowa.';
  }

  if (message.includes('with zero amount')) {
    return 'Dokument kasowy i powiązana płatność muszą mieć kwotę większą od zera.';
  }

  if (message.includes('does not match CashDocument') && message.includes('amount')) {
    return 'Kwota płatności nie zgadza się z kwotą dokumentu kasowego.';
  }

  if (message.includes('payment date') && message.includes('document date')) {
    return 'Data płatności nie zgadza się z datą dokumentu kasowego.';
  }

  if (message.includes('Dokument kasowy') && message.includes('nie ma wygenerowanego pliku PDF')) {
    return 'Ten dokument kasowy nie ma jeszcze wygenerowanego pliku PDF.';
  }

  return null;
};

const getMessageFromStatus = (status: number | string | undefined): string | null => {
  if (status === undefined) {
    return null;
  }

  if (status === 'FETCH_ERROR') {
    return 'Brak połączenia z API. Sprawdź dostępność backendu i sieć.';
  }

  if (status === 'PARSING_ERROR') {
    return 'Odpowiedź API ma nieprawidłowy format.';
  }

  if (status === 400) {
    return 'Dane dokumentu kasowego są nieprawidłowe albo nie spełniają reguł biznesowych.';
  }

  if (status === 401) {
    return 'Sesja wygasła. Zaloguj się ponownie.';
  }

  if (status === 403) {
    return 'Brak uprawnień do operacji na dokumentach kasowych.';
  }

  if (status === 404) {
    return 'Nie znaleziono wskazanego dokumentu kasowego lub powiązanej płatności.';
  }

  if (status === 409) {
    return 'Wystąpił konflikt danych dokumentu kasowego. Odśwież dane i spróbuj ponownie.';
  }

  if (status === 429) {
    return 'Zbyt wiele żądań. Odczekaj chwilę i ponów próbę.';
  }

  if (typeof status === 'number' && status >= 500) {
    return 'Wystąpił błąd serwera podczas operacji na dokumencie kasowym. Spróbuj ponownie później.';
  }

  return null;
};

export const getCashDocumentsErrorMessage = (error: unknown, fallbackMessage: string): string => {
  const errorCode = getProblemErrorCode(error);
  if (errorCode && CASH_DOCUMENTS_ERROR_MESSAGES[errorCode]) {
    return CASH_DOCUMENTS_ERROR_MESSAGES[errorCode];
  }

  const inferredMessage = getProblemMessages(error)
    .map(mapBackendMessageToOperatorMessage)
    .find((message): message is string => Boolean(message));
  if (inferredMessage) {
    return inferredMessage;
  }

  const statusMessage = getMessageFromStatus(getErrorStatus(error));
  if (statusMessage) {
    return statusMessage;
  }

  return extractApiErrorMessage(error, fallbackMessage);
};

export const getCashDocumentsErrorCode = (error: unknown): string | undefined => {
  return getProblemErrorCode(error);
};
